import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import '@v2/styles/tailwind.css';
import { billingAuditService } from '@v2/services';
import { queryKeys } from '@v2/services';
import type { BillingAuditQuery } from '@v2/types';
import BillingAuditTable from './BillingAuditTable';
import BillingAuditFilters from './BillingAuditFilters';

function defaultSince(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T00:00:00Z`;
}

const PAGE_SIZE = 50;

const BillingAuditPage: React.FC = () => {
  const [filters, setFilters] = useState<BillingAuditQuery>({
    since: defaultSince(),
    page: 1,
    pageSize: PAGE_SIZE,
  });

  const queryParams = useMemo(
    () => ({ ...filters, page: filters.page ?? 1, pageSize: PAGE_SIZE }),
    [filters],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.billingAudit.list(queryParams as Record<string, unknown>),
    queryFn: () => billingAuditService.listBillingAudit(queryParams),
    keepPreviousData: true,
  });

  const currentPage = filters.page ?? 1;
  const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;

  const handleFiltersChange = (next: BillingAuditQuery) => setFilters(next);

  const goToPage = (p: number) => setFilters(f => ({ ...f, page: p }));

  return (
    <div className="tw-p-4">
      <h1 className="tw-text-2xl tw-font-semibold tw-text-gray-800 tw-mb-4">
        Billing Metadata Audit Log
      </h1>

      <BillingAuditFilters filters={filters} onChange={handleFiltersChange} />

      <BillingAuditTable
        entries={data?.items ?? []}
        loading={isLoading}
        error={error instanceof Error ? error.message : error ? String(error) : null}
      />

      {totalPages > 1 && (
        <div className="tw-flex tw-items-center tw-justify-between tw-mt-4">
          <span className="tw-text-sm tw-text-gray-500">
            Page {currentPage} of {totalPages} ({data?.totalCount} total)
          </span>
          <div className="tw-flex tw-gap-2">
            <button
              className="tw-px-3 tw-py-1 tw-text-sm tw-rounded tw-border tw-border-gray-300 disabled:tw-opacity-40"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
              aria-label="Previous page">
              Prev
            </button>
            <button
              className="tw-px-3 tw-py-1 tw-text-sm tw-rounded tw-border tw-border-gray-300 disabled:tw-opacity-40"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
              aria-label="Next page">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingAuditPage;
